import {Controller, FieldError, FieldValues, Path, RegisterOptions, useFormContext} from "react-hook-form";

type ModalTextAreaInputProps<T extends FieldValues = FieldValues> = {
    label: string;
    disabled: boolean;
    required?: boolean;
    requiredMessage?: string;
    field: keyof T;
    rowsNum?: number;
    maxLength?: number;
    showCharacterCount?: boolean;
    labelPosition?: 'left' | 'top';
    shouldStopInputOnMaxLength?: boolean;
    placeholder?: string;
    optionalRules?: RegisterOptions;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
};

const maxLengthValidator = (maxLength: number) => (v: any) => {
    return (v || '').length <= maxLength || `Maximum length is ${maxLength} characters`;
};

const notEmptyValidator = (requiredMessage: string) => (v: any) => {
    return (v || '').trim().length > 0 || requiredMessage;
};

export function ModalTextAreaInput<T extends FieldValues = FieldValues>({
                                          label,
                                          disabled,
                                          field,
                                          required = true,
                                          rowsNum = 3,
                                          maxLength = 2048,
                                          showCharacterCount = false,
                                          labelPosition = 'left',
                                          shouldStopInputOnMaxLength = false,
                                          requiredMessage='Required',
                                          placeholder,
                                          optionalRules,
                                          className,
                                          inputClassName,
                                          labelClassName,
                                      }: ModalTextAreaInputProps<T>) {
    const {
        control,
        formState,
        trigger,
    } = useFormContext<T>();
    const errors = formState.errors as Record<keyof T, FieldError>;

    const isLabelTop = labelPosition === 'top';

    let rules: RegisterOptions = {required: {value: required, message: requiredMessage}};
    const builtInValidators: Record<string, any> = {
        maxLength: maxLengthValidator(maxLength)
    };
    if (required) {
        builtInValidators.notEmpty = notEmptyValidator(requiredMessage);
    }
    if (optionalRules) {
        rules = optionalRules;
    } else {
        rules.validate = builtInValidators;
    }

    const renderInput = () => (
        <Controller
            name={field as string as Path<T>}
            control={control}
            rules={rules}
            render={({field: {onChange, onBlur, value}}) => (
                <div className="w-full">
                    <textarea
                        className={`w-full p-2 border rounded focus:outline-none focus:border-gray-500 ${
                            errors[field] ? 'border-red-500' : 'border-gray-300'
                        } ${disabled ? 'bg-gray-100' : 'hover:border-atlantis-100'}`}
                        rows={rowsNum}
                        value={value || ''}
                        onChange={(e) => { onChange(e.target.value); trigger(field as string as Path<T>); }}
                        onBlur={onBlur}
                        disabled={disabled}
                        maxLength={shouldStopInputOnMaxLength ? maxLength : undefined}
                        placeholder={placeholder}
                    />
                    {errors[field] && (
                        <div className="text-red-700 mt-1">{errors[field].message}</div>
                    )}
                    {showCharacterCount && maxLength && (
                        <div className="text-right text-sm text-gray-500 mt-1">
                            {String(value || '').length}/{maxLength} characters
                        </div>
                    )}
                </div>
            )}
        />
    );

    const renderLabel = () => (
        <>
            {label}
        </>
    );

    return (
        <div className={`w-full text-black-200 py-2 text-base items-center ${className || ''}`}>
            {isLabelTop ? (
                // Top label layout
                <div className="w-full">
                    <div className={`mb-2 ${labelClassName ?? ""}`}>
                        {renderLabel()}
                    </div>
                    {renderInput()}
                </div>
            ) : (
                // Left label layout
                <div className="flex items-start">
                    <div className={`w-2/6 text-right flex items-center justify-end pt-2 ${labelClassName ?? ""}`}>
                        {renderLabel()}
                    </div>
                    <div className={`w-4/6 ml-6 ${inputClassName || ''}`}>
                        {renderInput()}
                    </div>
                </div>
            )}
        </div>
    );
}
